import clsx from 'clsx';
import React, { useEffect } from 'react';
import { useParams } from 'react-router';

import SiteAvatar from '@/src/components/common/Avatar/SiteAvatar/SiteAvatar';
import SectionLoader, {
  LOADER_TYPE,
} from '@/src/components/common/Elements/Spinner/SectionLoader/SectionLoader';
import HighlightSearchTerm from '@/src/components/project/Common/Typography/HighlightSearchTerm';
import { isResponseError } from '@/src/components/project/Inbox2/api/config';
import { getSendersLabel } from '@/src/components/project/Inbox2/components/Sidebar/InboxEntry/EmailThreadEntry/EmailSenders/EmailSenders';
import {
  EmailEntry,
  EmailEntryListItem,
  EmailEntryToUpdateStatus,
  EmailEntryType,
  UserWithFullNameAndEmailAndAvatar,
} from '@/src/components/project/Inbox2/types';
import useInboxSearchParams from '@/src/components/project/Inbox2/useInboxSearchParams';
import { queryClient } from '@/src/lib/react-query/react-query';

import EmailEntryExpandedContent from './EmailEntryExpandedDetails';

import useSyncInboxEntry from '../../../../../useSyncInboxEntry';
import { emailEntriesKeys } from '../../../../api/getEmailEntries';
import { useMarkRead } from '../../../../api/markReadEmail';
import { EMAIL_THREAD_PAGE_LIMIT } from '../EmailEntries';
import { useMappedEmailEntry } from '../EmailEntry/useMappedEmailEntry';
import EmailEntrySummary from '../EmailEntrySummary/EmailEntrySummary';

export const getEmailEntryToUpdateStatus = (
  emailEntryListItem: EmailEntryListItem | EmailEntry
): EmailEntryToUpdateStatus => {
  if (emailEntryListItem.emailType === EmailEntryType.SENT) {
    return null;
  }

  return {
    id: emailEntryListItem.id,
    inboxEntryId: emailEntryListItem.inboxEntryId,
    status: emailEntryListItem.status,
    files: emailEntryListItem.files.list,
    allowedStatusChanges: emailEntryListItem.allowedStatusChanges,
    allowClose: emailEntryListItem.allowClose,
    duplicate: emailEntryListItem.duplicate,
  };
};

export const getUserDisplay = (from: UserWithFullNameAndEmailAndAvatar[]) => {
  const fromFullNames = from.map((from) => from.fullName || from.emailAddress);
  const fromFullNamesLabel = fromFullNames.join(', ');
  const defaultUser = from[0];

  if (fromFullNames.length < 1) {
    return (
      <React.Fragment>
        <SiteAvatar name={defaultUser.emailAddress} disableTooltip={true} />
        <p className={clsx('tw-font-brandonTextMedium tw-text-cfo-black', 'tw-ml-[10px]')}>
          <HighlightSearchTerm>{fromFullNamesLabel}</HighlightSearchTerm>
        </p>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <SiteAvatar
        name={`${fromFullNamesLabel}`}
        disableTooltip={true}
        avatar={defaultUser.avatar}
      />
      <p className={clsx('tw-font-brandonTextMedium tw-text-cfo-black', 'tw-ml-[10px]')}>
        <HighlightSearchTerm>{fromFullNamesLabel}</HighlightSearchTerm>
      </p>
    </React.Fragment>
  );
};

export const getEmailTypeDisplay = (emailType) => {
  return emailType[0].toUpperCase() + emailType.slice(1).toLowerCase();
};

type EmailEntryExpandedProps = {
  emailEntryListItem: EmailEntryListItem;
  isLastItem?: boolean;
  onClick: (emailEntry: EmailEntry) => void;
};

const EmailEntryExpanded = ({
  emailEntryListItem,
  onClick,
  isLastItem = false,
}: EmailEntryExpandedProps) => {
  const { projectId } = useParams<{ projectId: string }>();
  const { isTrash } = useInboxSearchParams();
  const { emailEntry, isLoading } = useMappedEmailEntry({
    projectId,
    emailId: emailEntryListItem.id,
    inboxEntryId: emailEntryListItem.inboxEntryId,
  });
  const syncInboxEntry = useSyncInboxEntry();
  const markReadMutation = useMarkRead({
    projectId,
    inboxEntryId: emailEntryListItem.inboxEntryId,
    emailId: emailEntryListItem.id,
  });

  // Email opened listener
  useEffect(() => {
    if (!emailEntryListItem) {
      return;
    }

    if (!emailEntryListItem.isRead) {
      markReadMutation
        .mutateAsync({
          emailId: emailEntryListItem.id,
          inboxEntryId: emailEntryListItem.inboxEntryId,
          projectId,
          read: true,
        })
        .then((response) => {
          if (isResponseError(response)) {
            return;
          }

          queryClient.setQueryData(
            [
              ...emailEntriesKeys.lists({
                projectId,
                inboxEntryId: emailEntryListItem.inboxEntryId,
                trash: isTrash,
              }),
              EMAIL_THREAD_PAGE_LIMIT,
            ],
            (prev: any) => {
              if (!prev) {
                return prev;
              }

              return {
                ...prev,
                pages: prev.pages.map((page) => {
                  return {
                    ...page,
                    data: {
                      ...page.data,
                      emails: page.data.emails.map((emailEntry) => {
                        if (emailEntry.id === emailEntryListItem.id) {
                          return {
                            ...emailEntry,
                            status: response.data['inbox-email'].status,
                            read: response.data['inbox-email'].read,
                          };
                        }
                        return emailEntry;
                      }),
                    },
                  };
                }),
              };
            }
          );
          if (response.data['inbox-entry']) {
            syncInboxEntry(response.data['inbox-entry']);
          }
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading || !emailEntry) {
    return (
      <React.Fragment>
        <EmailEntrySummary
          key={`ees_${emailEntryListItem.id}`}
          emailEntryListItem={emailEntryListItem}
        >
          <SectionLoader loaderType={LOADER_TYPE.screen} />
        </EmailEntrySummary>
      </React.Fragment>
    );
  }

  return (
    <EmailEntryExpandedContent
      emailEntryListItem={emailEntryListItem}
      emailEntry={emailEntry}
      isLastItem={isLastItem}
      onClick={onClick}
    />
  );
};

export default EmailEntryExpanded;
